import { computed, action, observable, makeObservable } from 'mobx';

import { controlCenterApi } from '$src/api/control-center-api';
import { HTTP_STATUS_CODE_BAD_REQUEST, HTTP_STATUS_CODE_UNAUTHORIZED, NeighborPrincipalDomain } from '$src/api/constants';
import { getLoginURL, getLogoutURL } from '$src/utils/session';
import { redirectToURL } from '$src/utils/redirect';
import { Notify } from '$src/utils/notify';
import cloudwatchInitialize from '$src/utils/cloudwatchRUM';
import { mergeWith } from 'lodash';
import getFeaturesFromCookies from '$src/utils/cookies';
import getEnvironment from '$src/utils/enviroment';

export default class SessionStore {
  user = null;
  loading = null;
  featureFlags = getEnvironment() === 'gamma' ? getFeaturesFromCookies() : null;
  isAuthorized = null;
  csrfToken = '';
  isMobile = false;
  loadingStack = 0;
  neighborDomainConsulted = false;
  locationDomainConsulted = false;
  authorizationAccepted = false;

  fetchMe = async () => {
    this.setLoading(true);
    try {
      const { csrfToken, user_id: userId } = await controlCenterApi.getAccessToken();
      if (!userId || !csrfToken) Notify.error('User is unauthorized');
      else {
        this.setCSRFToken(csrfToken);
        this.setUser({ uid: userId });
        const featureFlags = await controlCenterApi.fetchFeatures(NeighborPrincipalDomain, this.user.uid);

        this.setFeatureFlags(featureFlags, NeighborPrincipalDomain);

        cloudwatchInitialize(userId, csrfToken);

      }
    } catch (error) {
      const status = error?.response?.status;
      if ([HTTP_STATUS_CODE_BAD_REQUEST, HTTP_STATUS_CODE_UNAUTHORIZED].includes(status)) {
        redirectToURL(getLoginURL());
        return;
      }
      Notify.error('Error getting access token', error);
      this.setUser(null);
    }
    this.setLoading(false);
  };

  onLogout = async () => {
    this.setLoading(null);
    try {
      const ringLogoutUrl = getLogoutURL();
      this.setUser(null);

      redirectToURL(ringLogoutUrl);
    } catch (e) {
      Notify.error('User failed to logout', e);
    }
  };

  setUser = (user) => {
    if (!user) {
      this.user = null;
    } else {
      this.user = { ...user, user_id: user.uid };
    }
  };

  setLoading = (value) => {
    this.loadingStack = value ? this.loadingStack + 1 : this.loadingStack - 1;
    this.loading = value;
  };

  setFeatureFlags = (featureFlags, domain) => {
    const featuresInCookies = getFeaturesFromCookies();
    this.featureFlags = featuresInCookies || mergeWith(this.featureFlags, featureFlags, (a, b) => ({ enabled: a?.enabled || b?.enabled }));

    if (domain === NeighborPrincipalDomain) {
      this.setNeighborDomainConsulted();
    } else {
      this.setLocationDomainConsulted();
    }
  };

  setCSRFToken = (token) => {
    this.csrfToken = token;
  };

  setIsMobile = (isMobile) => {
    this.isMobile = isMobile;
  };
  setAuthorizationAccepted = (value = true) => {
    this.authorizationAccepted = value;
    sessionStorage.setItem('authorizationAccepted', value);
  };

  setNeighborDomainConsulted = () => {
    this.neighborDomainConsulted = true;
  };

  setLocationDomainConsulted = () => {
    this.locationDomainConsulted = true;
  };

  get isLoading() {
    return this.loadingStack !== 0;
  }

  get allDomainsConsulted() {
    return this.neighborDomainConsulted && this.locationDomainConsulted;
  }
  get isAuthorizationAccepted() {
    return this.authorizationAccepted || JSON.parse(sessionStorage.getItem('authorizationAccepted')) === true;
  }
  authorizeVSG = async (redirectTo) => {
    // no-op
  }
  constructor(rootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      user: observable,
      loading: observable,
      featureFlags: observable,
      loadingStack: observable,
      csrfToken: observable,
      isMobile: observable,
      authorizationAccepted: observable,
      neighborDomainConsulted: observable,
      locationDomainConsulted: observable,
      fetchMe: action,
      onLogout: action,
      setUser: action,
      setLoading: action,
      setFeatureFlags: action,
      setIsMobile: action,
      setAuthorizationAccepted: action,
      setNeighborDomainConsulted: action,
      setLocationDomainConsulted: action,
      authorized: computed,
      isLoading: computed,
      allDomainsConsulted: computed,
      authorizeVSG: action,
    });
  }

  get authorized() {
    try {
      return this.loading === false && this.user.user_id;
    } catch (e) {
      this.setUser(null);
      return false;
    }
  }
}
